import { z } from "zod";
import { StudentOccurrences } from "./pair-occurrences.ts";
import { Lesson } from "./room.ts";
import { StudentPayment, StudentProfile } from "./student-profile.ts";
import { DisplayName, MeResponse, UserId } from "./user.ts";

/**
 * Приглашение постоянное и принадлежит УЧЕНИКУ, а не уроку (решение владельца
 * 09.08.2026, раздел 5.10 CLAUDE.md). Преподаватель заводит ученика, получает
 * одну ссылку и отдаёт её один раз. Дальше ученик ходит по ней на все занятия.
 *
 * Ссылка не «создаёт ученика», она отдаёт уже заведённого ученика тому, кто по
 * ней пришёл. Отсюда и риск, принятый осознанно: это долгоживущий секрет, и
 * утечка даёт доступ к архиву пары. Митигации: 128 бит энтропии, лимит частоты
 * погашения, кнопка перевыпуска и предложение привязать вход после урока.
 */
export const InviteCode = z.string().regex(/^[A-Za-z0-9_-]{22}$/);
export type InviteCode = z.infer<typeof InviteCode>;

export const CreateStudentRequest = z.object({
  displayName: DisplayName,
});
export type CreateStudentRequest = z.infer<typeof CreateStudentRequest>;

/**
 * Последняя работа ученика: подпись в списке выбора при мультивыдаче (5.4
 * docs/homework-phase2-design.md).
 *
 * ⚠️ Считается боковым запросом с ДВУМЯ предикатами (`teacher_user_id = :me` и
 * `deleted_at IS NULL`), и оба не украшение: модель проекта это многие ко
 * многим, а индекс работ начинается с ученика, то есть без первого предиката
 * преподаватель увидел бы название чужой домашки, а без второго ту, которую сам
 * положил в корзину.
 */
export const StudentLastHomework = z.object({
  title: z.string(),
  createdAt: z.number().int(),
  state: z.enum(["not_opened", "in_progress", "done"]),
  /**
   * Срок последней работы. Без него список учеников не отличает «висит» от
   * «просрочено», а это ровно тот вопрос, ради которого колонка и заведена:
   * «не открыта» у работы со сроком через неделю и у работы, просроченной на
   * три дня, это разные новости.
   *
   * `null` означает «работа без срока», отсутствие поля означает «сервер
   * старый»: web едет после API, и вкладка, открытая в окно выкатки, не должна
   * падать на разборе.
   */
  dueAt: z.number().int().nullable().optional(),
});
export type StudentLastHomework = z.infer<typeof StudentLastHomework>;

export const StudentSummary = z.object({
  userId: UserId,
  displayName: DisplayName,
  createdAt: z.number().int(),
  /** Ученик видит архив своих уроков, пока преподаватель это не закрыл. */
  archiveVisible: z.boolean(),
  unlinked: z.boolean(),
  /** Гость ещё не привязал вход: его cookie это единственный ключ к истории. */
  claimed: z.boolean(),
  inviteActive: z.boolean(),
  /**
   * Дата последнего занятия пары. Первый признак потока в списке выбора: потоки
   * различаются днями недели, и раздачей этот признак не портится, в отличие от
   * подписи про последнюю работу (5.4 фазы 2).
   *
   * ⚠️ Это НЕ `createdAt`: там дата создания пары, и путать их нельзя.
   */
  lastLessonAt: z.number().int().nullable().optional(),
  /** `null` означает «работ ещё не было». Отсутствие поля означает «сервер старый». */
  lastHomework: StudentLastHomework.nullable().optional(),
  /**
   * Ближайшее не отменённое занятие пары, тот же расчёт, что у ручки
   * `/v1/schedule/next-occurrence` (её же считает форма выдачи домашки).
   *
   * Здесь, а не отдельным запросом на строку, потому что вопрос «когда мы
   * встречаемся» задают ко ВСЕМУ списку сразу, а N запросов по числу учеников
   * на канале 128 кбит/с это N кругов по сети ради одной колонки.
   *
   * ⚠️ Считается по расписанию, а не по созданным урокам: занятие может быть
   * отменено или перенесено, и раскрытие серий «по правилу» показало бы то,
   * чего не будет. `null` означает «расписания у пары нет».
   */
  nextLessonAt: z.number().int().nullable().optional(),
});
export type StudentSummary = z.infer<typeof StudentSummary>;

/**
 * Код возвращается ровно один раз, в ответе на создание или перевыпуск.
 * В базе лежит только SHA-256: потерянную ссылку не восстанавливают, а
 * перевыпускают, и это правильный ответ на «а покажите ссылку ещё раз».
 */
export const StudentWithInvite = StudentSummary.extend({
  inviteCode: InviteCode,
  inviteUrl: z.string().url(),
});
export type StudentWithInvite = z.infer<typeof StudentWithInvite>;

export const StudentListResponse = z.object({
  students: z.array(StudentSummary),
});
export type StudentListResponse = z.infer<typeof StudentListResponse>;

/**
 * Судьба постоянной ссылки: то, что о ней можно сказать, не показывая кода.
 *
 * Самого кода здесь нет и быть не может: в базе только SHA-256, и второй раз
 * он не показывается никогда. Поэтому экран ученика показывает не ссылку, а её
 * состояние: когда выпущена, открывал ли её ученик, сколько раз и не отозвана
 * ли она.
 *
 * ⚠️ Это ПОСЛЕДНЯЯ ссылка пары, а не активная. «Ссылку не выпускали» и «ссылку
 * отозвали» это разные слова на экране, а различить их можно только по
 * отозванной строке: активной в обоих случаях нет.
 */
export const StudentInviteInfo = z.object({
  issuedAt: z.number().int(),
  /** Когда ученик открыл её впервые. `null` это «выдана, но ещё не открыта». */
  firstUsedAt: z.number().int().nullable(),
  lastUsedAt: z.number().int().nullable(),
  /** Сколько раз погашена. Больше единицы это обычно второе устройство. */
  useCount: z.number().int(),
  revokedAt: z.number().int().nullable(),
});
export type StudentInviteInfo = z.infer<typeof StudentInviteInfo>;

/**
 * Как справляется: попытки ответов по всем занятиям этой пары.
 *
 * Две величины, а не готовая доля: делит и округляет клиент, потому что «0 из
 * 0» и «0 из 40» это разные сообщения, а процент их уравнивает.
 *
 * ⚠️ `attempts` это попытки С ВЕРДИКТОМ: попытка перевода (вердикт выносит
 * человек) не входит ни сюда, ни в `correct`.
 */
export const StudentProgress = z.object({
  attempts: z.number().int(),
  correct: z.number().int(),
});
export type StudentProgress = z.infer<typeof StudentProgress>;

/**
 * Экран ученика одним ответом.
 *
 * Одним, а не тремя: карточка открывается по переходу из списка, и три круга
 * по сети на канале 128 кбит/с (раздел 3.1 CLAUDE.md) человек видит как
 * подвисание. Уроки отбирает база по `lesson_student_recent_idx`, а не браузер
 * по общему списку: у преподавателя с историей их тысячи.
 */
export const StudentDetail = z.object({
  student: StudentSummary,
  invite: StudentInviteInfo.nullable(),
  /**
   * ПЕРВАЯ страница занятий пары, свежие сверху. Остальные приезжают ручкой
   * `GET /v1/students/:id/lessons` по курсору `lessonsNextCursor`.
   */
  lessons: z.array(Lesson),
  /**
   * Курсор следующей страницы занятий; `null` значит «дальше ничего нет».
   * Необязателен по той же причине, что `profile` ниже: отсутствие это старый
   * API, который отдавал список целиком.
   */
  lessonsNextCursor: z.string().nullable().optional(),
  /** Сколько занятий у пары всего. Счётчик в заголовке берёт его, а не длину. */
  lessonCount: z.number().int().optional(),
  progress: StudentProgress,
  /**
   * Анкета пары: контакты, режим оплаты, приватная заметка.
   *
   * ⚠️ Необязательна, и это не забывчивость: web едет отдельно от API, и вкладка
   * со старым бандлом это поле молча срежет, а новый бандл на старом API его не
   * получит. Отсутствие означает «не знаем» и рисуется как пустая анкета, то
   * есть ровно как карточка выглядела до появления этой работы.
   */
  profile: StudentProfile.optional(),
  /**
   * Учёт оплат. По той же причине необязателен.
   *
   * ⚠️ Считается ТОЛЬКО у пары, где режим оплаты задан. У `none` сюда приезжает
   * пустой объект без единого занятия, и стоит это ноль запросов: раскрытие
   * расписания в прошлое не запускается вовсе.
   */
  payment: StudentPayment.optional(),
  /**
   * Занятия пары одним списком (решение владельца 26.09.2026): первая страница
   * истории, число занятий и ближайшие созданные уроки. Остальные страницы
   * ручкой `GET /v1/students/:id/occurrences`.
   *
   * ⚠️ Заменяет `lessons`, `lessonsNextCursor`, `lessonCount` и
   * `payment.occurrences`, но те НЕ сняты в этой же правке: вкладка со старым
   * бандлом живёт после выкатки, expand → contract. Необязательно по той же
   * причине: отсутствие это старый API.
   */
  occurrences: StudentOccurrences.optional(),
});
export type StudentDetail = z.infer<typeof StudentDetail>;

/**
 * Страница занятий пары на карточке ученика. Первая страница едет в самой
 * карточке, размер тот же. Размер выбран по экрану, а не по сети: под списком
 * живёт «Доступ» со ссылкой, и двести строк увозили его за сотни строк вниз.
 * `limit` вне границ это `invalid_query`.
 */
export const STUDENT_LESSON_PAGE_LIMIT_DEFAULT = 10;
export const STUDENT_LESSON_PAGE_LIMIT_MAX = 50;
/**
 * Шаг догрузки: вторая и следующие страницы, то есть умолчание ручки
 * `/lessons`. Крупнее первой, потому что её уже попросили руками: кнопка
 * означает «хочу дальше», а не «покажи ещё чуть-чуть».
 */
export const STUDENT_LESSON_MORE_LIMIT = 25;

export const StudentLessonsResponse = z.object({
  lessons: z.array(Lesson),
  nextCursor: z.string().nullable(),
});
export type StudentLessonsResponse = z.infer<typeof StudentLessonsResponse>;

/**
 * Правка ученика преподавателем. Сегодня в ней ровно одно поле.
 *
 * Видимость архива до сих пор жила только в схеме и в проверке прав
 * (`auth/access.ts`), а поменять её было нечем: закрыть архив можно было
 * только вместе с отвязкой. Это отдельное решение преподавателя, и у него
 * теперь отдельный переключатель.
 */
export const UpdateStudentRequest = z.object({
  archiveVisible: z.boolean(),
});
export type UpdateStudentRequest = z.infer<typeof UpdateStudentRequest>;

/**
 * Ответ погашения ссылки приглашения.
 *
 * ⚠️ **Расширяет `MeResponse`, а не повторяет его поля**, и это исправление от
 * 21.08.2026. Клиент принимает этот ответ ЗА СЕССИЮ целиком и рисует по нему
 * кабинет, не сходив за `/v1/auth/me`. Пока схема была самостоятельной, она
 * молча срезала всё, чего в ней не перечислили, и ловилось это по одному полю
 * за раз: сначала `timeZone` (ученик, уже подтвердивший пояс, после каждого
 * погашения снова видел полосу «укажите часовой пояс»), потом стороны пар
 * (сайдбар преподавателя у ученика до первой перезагрузки), а `hasPassword` и
 * `tracking` не приезжали вовсе, из-за чего у ученика с паролем пропадал пункт
 * «Сменить пароль». Сервер при этом отдавал их честно: `meBody` собирает одно
 * тело на все шесть ручек, а срезала их эта схема.
 *
 * Наследование закрывает класс целиком: поле, добавленное в `MeResponse`,
 * доезжает сюда само.
 */
export const RedeemInviteResponse = MeResponse.extend({
  token: z.string().min(1),
  teacherUserId: UserId,
  teacherDisplayName: DisplayName,
  /**
   * Имя ученика на паре ЭТОЙ ссылки (10.1 и 9.5 docs/account-adopt-design.md):
   * приветствие берёт его, а не `displayName`, потому что держатель ключа
   * преподавателя читает имя, которое вписал этот преподаватель.
   *
   * ⚠️ Три поля ниже необязательны ради старого API под новым бандлом.
   */
  pairDisplayName: DisplayName.optional(),
  /**
   * Преподаватели, чьи занятия теперь видны в этом браузере, по алфавиту:
   * живые пары, видимые сессии (у сессии без набора все живые пары).
   */
  scopeTeachers: z.array(DisplayName).optional(),
  /**
   * Набор сессии вырос этим погашением: тот же человек открыл ссылку ещё
   * одного преподавателя. Только тогда приветствие говорит итог (9.5).
   */
  scopeGrew: z.boolean().optional(),
});
export type RedeemInviteResponse = z.infer<typeof RedeemInviteResponse>;

/**
 * Ссылка на ОДНО занятие 1:1: `/lesson/<id>?k=<ключ>`.
 *
 * В отличие от приглашения выше, ключ принадлежит уроку, а не ученику, и живёт
 * он недолго: годится только в окне вокруг времени занятия (правило окна и его
 * довод в `apps/api/src/lesson-link-window.ts`). Поэтому в базе он лежит
 * открытым текстом, и преподаватель копирует его заново в любой момент урока.
 *
 * 128 бит в base64url, то есть ровно 22 символа, как у кода приглашения.
 */
export const LessonLinkKey = z.string().regex(/^[A-Za-z0-9_-]{22}$/);
export type LessonLinkKey = z.infer<typeof LessonLinkKey>;

/**
 * Ответ преподавателю: готовая ссылка, которую он отправляет ученику, и окно,
 * в котором она работает (миллисекунды эпохи, границы включены). Окно едет
 * с сервера, а не считается на клиенте: правило живёт в одном месте
 * (`apps/api/src/lesson-link-window.ts`).
 */
export const StudentLessonLinkResponse = z.object({
  url: z.string().url(),
  opensAt: z.number().int(),
  closesAt: z.number().int(),
});
export type StudentLessonLinkResponse = z.infer<typeof StudentLessonLinkResponse>;

/**
 * Погашение ключа занятия. Ключ едет ТЕЛОМ, а не в пути: путь запроса попадает
 * в журналы и в буфер последних отказов, а тело нет.
 */
export const RedeemLessonLinkRequest = z.object({
  key: z.string().max(64),
});
export type RedeemLessonLinkRequest = z.infer<typeof RedeemLessonLinkRequest>;
